import { useEffect } from "react";
import { PhoneCall } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import SummaryCaptureForm from "@/components/voice-genie/SummaryCapture/SummaryCaptureForm";
import { useSummaryCapture } from "@/hooks/voice/useSummaryCapture";

interface HumanHandoffPromptProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandColor: string;
  agentName?: string;
  isConnecting: boolean;
  error: string | null;
  onConfirm: () => void;
  agentId?: string;
  agentUserId?: string;
  onSummaryCaptured?: (email: string) => void;
}

/**
 * Confirms before ending the AI voice session and connecting the visitor to
 * a live human call. Ending the AI session is destructive (mic is released,
 * conversation state is gone), so this always confirms -- both when the AI
 * itself asked to hand off and when the visitor tapped the manual button.
 *
 * When the handoff itself fails (`error` set), this is a dead end for the
 * visitor — no human, and the AI session may already be gone — so it offers
 * the same "leave your email, we'll come back to you" ask as VoiceTroubleSheet
 * instead of leaving them stuck on a red error line.
 */
export const HumanHandoffPrompt = ({
  open,
  onOpenChange,
  brandColor,
  agentName,
  isConnecting,
  error,
  onConfirm,
  agentId,
  agentUserId,
  onSummaryCaptured,
}: HumanHandoffPromptProps) => {
  const capture = useSummaryCapture({
    agentId,
    agentUserId,
    source: "handoff",
    onCaptured: onSummaryCaptured,
  });

  useEffect(() => {
    if (open && error && agentId) capture.markShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, error, agentId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogTitle className="sr-only">Talk to a human</DialogTitle>
        <div className="flex flex-col items-center px-2 py-4 text-center">
          <span
            className="mb-3 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ background: `${brandColor}1a`, color: brandColor }}
          >
            <PhoneCall size={20} strokeWidth={2} />
          </span>
          <h3 className="text-[16px] font-semibold text-[#222B45]">Connect you with a human?</h3>
          <p className="mt-1.5 text-[13px] leading-relaxed text-[#64748B]">
            {agentName ? `This will end your conversation with ${agentName}` : "This will end your current conversation"}{" "}
            and connect you to a live phone call.
          </p>

          {error && (
            <>
              <p className="mt-3 text-[13px] font-medium text-red-500" role="alert">
                {error}
              </p>
              {agentId && (
                <div className="mt-3 w-full rounded-xl border border-[#E5EAF0] bg-[#F9FAFC] p-3.5 text-left">
                  <p className="mb-2.5 text-[13px] leading-snug text-[#3D4759]">
                    Leave your email and we'll come back to you.
                  </p>
                  <SummaryCaptureForm
                    brandColor={brandColor}
                    values={capture.values}
                    onChange={capture.onChange}
                    expanded={false}
                    onToggleExpanded={() => {}}
                    onSubmit={capture.submit}
                    isSubmitting={capture.isSubmitting}
                    submitted={capture.submitted}
                    error={capture.error}
                    hideHeader
                    hideExpandToggle
                    confirmationTitle="✓ Got it"
                    confirmationBody="We'll follow up by email."
                    alreadyCapturedEmail={
                      capture.alreadyCaptured && !capture.editing ? capture.capturedEmail : null
                    }
                    onChangeEmail={capture.startEdit}
                  />
                </div>
              )}
            </>
          )}

          <div className="mt-5 flex w-full gap-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isConnecting}
              className="flex-1 rounded-xl border border-[#E5EAF0] px-4 py-2.5 text-[14px] font-semibold text-[#475569] transition-colors hover:bg-[#F5F7FA] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isConnecting}
              className="flex-1 rounded-xl px-4 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: brandColor }}
            >
              {isConnecting ? "Connecting…" : "Connect me"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HumanHandoffPrompt;
